import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ClubApi } from "../../services/clubApi";
import { devLog } from "../../utils/devLog";

const MOBILE_ARCHIVE_EPISODES_PER_PAGE = 4;
const DESKTOP_ARCHIVE_EPISODES_PER_PAGE = 6;

const slugify = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildArchiveEpisodePath = (archiveNumber: number, episodeNumber: number, title = "") => {
  const slug = slugify(title);
  return slug
    ? `/agency/archive/${archiveNumber}/episodes/${episodeNumber}/${slug}`
    : `/agency/archive/${archiveNumber}/episodes/${episodeNumber}`;
};

const formatEpisodeLabel = (archiveNumber: number, episodeNumber: number) =>
  `Archive ${archiveNumber} Episode ${String(episodeNumber).padStart(2, "0")}`;

const formatEpisodeNavTitle = (title = "", archiveNumber = 0, episodeNumber = 0) =>
  String(title || "").trim() || formatEpisodeLabel(archiveNumber, episodeNumber);

const formatArchiveHeadingTitle = (value = "") => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return normalized.replace(/\s*-\s*Episode\s+\d+\s*$/i, "").trim() || normalized;
};

const formatArchiveEpisodeTag = (episodeNumber = 0) =>
  `Episode ${String(Number(episodeNumber || 0)).padStart(2, "0")}`;

const formatArchiveEpisodeTitle = (value = "") => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return normalized.replace(/^Episode\s+\d+\s*:\s*/i, "").trim() || normalized;
};

const getArchiveEpisodesPerPage = () => {
  if (typeof window === "undefined") return DESKTOP_ARCHIVE_EPISODES_PER_PAGE;
  return window.innerWidth < 1024 ? MOBILE_ARCHIVE_EPISODES_PER_PAGE : DESKTOP_ARCHIVE_EPISODES_PER_PAGE;
};

const getArchiveEpisodeSequenceNumber = (archiveNumber: number, episodeNumber: number) => {
  const normalizedArchiveNumber = Number(archiveNumber || 0);
  const normalizedEpisodeNumber = Number(episodeNumber || 0);

  if (!Number.isInteger(normalizedArchiveNumber) || normalizedArchiveNumber < 1) return null;
  if (!Number.isInteger(normalizedEpisodeNumber) || normalizedEpisodeNumber < 1) return null;

  return ((normalizedArchiveNumber - 1) * 24) + normalizedEpisodeNumber;
};

const getArchivePositionFromSequence = (sequenceNumber: number, archiveDefinitions: any[] = []) => {
  let remaining = Number(sequenceNumber || 0);
  if (!Number.isInteger(remaining) || remaining < 1) return null;

  let traversed = 0;
  for (const archive of archiveDefinitions) {
    const plannedEpisodeCount = Number(archive?.plannedEpisodeCount || 0);
    if (!Number.isInteger(plannedEpisodeCount) || plannedEpisodeCount < 1) continue;

    if (remaining <= plannedEpisodeCount) {
      return {
        archiveNumber: Number(archive?.archiveNumber || 0),
        archiveTitle: String(archive?.title || `Archive ${archive?.archiveNumber || ""}`).trim(),
        episodeNumber: remaining,
        sequenceNumber,
        isAuthored: remaining <= Number(archive?.authoredEpisodeCount || 0),
      };
    }

    remaining -= plannedEpisodeCount;
    traversed += plannedEpisodeCount;
  }

  const lastArchive = archiveDefinitions[archiveDefinitions.length - 1];
  if (!lastArchive) return null;

  return {
    archiveNumber: Number(lastArchive?.archiveNumber || 0) + 1,
    archiveTitle: `Archive ${Number(lastArchive?.archiveNumber || 0) + 1}`,
    episodeNumber: sequenceNumber - traversed,
    sequenceNumber,
    isAuthored: false,
  };
};

const buildContentBlocks = (bodyMarkdown = "") => {
  const blocks = String(bodyMarkdown || "")
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);

  return blocks.map((block, index) => ({
    id: `${index}-${block.slice(0, 24)}`,
    type: block === "---" ? "scene_break" : "paragraph",
    content: block,
  }));
};

export const renderArchiveInlineFormatting = (text = "") => {
  const value = String(text || "");
  const nodes: Array<string | JSX.Element> = [];
  const pattern = /(\*\*([^*]+)\*\*)|(\*([^*\n]+)\*)|(\"([^\"]+)\")/g;
  let lastIndex = 0;
  let matchIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(value)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(value.slice(lastIndex, match.index));
    }

    if (match[2]) {
      nodes.push(
        <strong key={`archive-bold-${matchIndex}`} className="font-semibold text-[#f2eee4]">
          {match[2]}
        </strong>
      );
    } else if (match[4]) {
      nodes.push(
        <strong key={`archive-bold-${matchIndex}`} className="font-semibold text-[#f2eee4]">
          {match[4]}
        </strong>
      );
    } else if (match[6]) {
      nodes.push(
        <em key={`archive-quote-${matchIndex}`} className="italic text-[#d7cfbb]">
          {`"${match[6]}"`}
        </em>
      );
    }

    lastIndex = pattern.lastIndex;
    matchIndex += 1;
  }

  if (lastIndex < value.length) {
    nodes.push(value.slice(lastIndex));
  }

  return nodes.length > 0 ? nodes : [value];
};

const toEmbedUrl = (url = "") => {
  const value = String(url || "").trim();
  if (!value) return "";

  const vimeoMatch = value.match(/^https?:\/\/(?:www\.)?vimeo\.com\/(\d+)(?:$|[/?#])/i);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  const youtubeMatch = value.match(
    /^https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&#?/]+)/
  );
  if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}`;

  return value;
};

const ClubArchiveEpisodePage = () => {
  const params = useParams();
  const archiveNumber = Number(params.archiveNumber || 0);
  const episodeNumber = Number(params.episodeNumber || 0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [archiveState, setArchiveState] = useState<any>(null);
  const [episodePayload, setEpisodePayload] = useState<any>(null);
  const [episodesPerPage, setEpisodesPerPage] = useState(getArchiveEpisodesPerPage);
  const [archiveEpisodePage, setArchiveEpisodePage] = useState(0);
  const lastSyncedArchivePageKey = useRef("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");
        devLog("archive", "episode_page_load_started", {
          archiveNumber,
          episodeNumber,
          route: window.location.pathname,
        });
        const response = await ClubApi.getArchiveEpisode(archiveNumber, episodeNumber);
        if (cancelled) return;
        setArchiveState(response.archiveState);
        setEpisodePayload(response.episode);
        devLog("archive", "episode_page_load_succeeded", {
          archiveNumber,
          episodeNumber,
          route: window.location.pathname,
          currentEpisodeLabel: response.archiveState?.currentEpisodeLabel,
          unlockedEpisodeCount: response.archiveState?.unlockedEpisodeCount,
          title: response.episode?.title,
        });
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.response?.data?.error || "Failed to load archive episode");
        setEpisodePayload(null);
        devLog("archive", "episode_page_load_failed", {
          archiveNumber,
          episodeNumber,
          route: window.location.pathname,
          status: err?.response?.status,
          message: String(err?.message || ""),
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [archiveNumber, episodeNumber]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => {
      setEpisodesPerPage(getArchiveEpisodesPerPage());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const access = useMemo(() => {
    const currentSequence = Number(archiveState?.unlockedEpisodeCount || 0);
    const currentArchive = Number(archiveState?.currentArchiveNumber || 0);
    const currentEpisode = Number(archiveState?.currentEpisode?.episodeNumber || 0);
    const requestedSequence = getArchiveEpisodeSequenceNumber(archiveNumber, episodeNumber);
    const isCurrent =
      currentArchive === archiveNumber && currentEpisode === episodeNumber;
    const isUnlocked = Boolean(requestedSequence && requestedSequence <= currentSequence);

    return {
      isCurrent,
      isUnlocked,
      isAuthored: Boolean(episodePayload),
      canOpen: Boolean(episodePayload && isUnlocked),
    };
  }, [archiveNumber, archiveState, episodeNumber, episodePayload]);

  const contentBlocks = useMemo(
    () => buildContentBlocks(String(episodePayload?.bodyMarkdown || "")),
    [episodePayload?.bodyMarkdown]
  );
  const briefingVideoEmbedUrl = useMemo(
    () => toEmbedUrl(String(episodePayload?.briefingVideoUrl || "")),
    [episodePayload?.briefingVideoUrl]
  );
  const archiveDefinitions = useMemo(
    () => (Array.isArray(archiveState?.archiveDefinitions) ? archiveState.archiveDefinitions : []),
    [archiveState?.archiveDefinitions]
  );
  const unlockedEpisodes = useMemo(() => {
    if (Array.isArray(archiveState?.unlockedEpisodes) && archiveState.unlockedEpisodes.length > 0) {
      return archiveState.unlockedEpisodes
        .filter((episode: any) => episode?.isAuthored)
        .map((episode: any) => ({
          archiveNumber: Number(episode.archiveNumber || 0),
          archiveTitle: String(episode.archiveTitle || "").trim(),
          episodeNumber: Number(episode.episodeNumber || 0),
          sequenceNumber: Number(episode.sequenceNumber || 0),
          isAuthored: Boolean(episode.isAuthored),
          title: String(episode.title || "").trim(),
          imageUrl: String(episode.imageUrl || "").trim(),
          label: formatEpisodeLabel(
            Number(episode.archiveNumber || 0),
            Number(episode.episodeNumber || 0)
          ),
          path: buildArchiveEpisodePath(
            Number(episode.archiveNumber || 0),
            Number(episode.episodeNumber || 0),
            String(episode.title || "")
          ),
          isCurrent:
            Number(episode.archiveNumber || 0) === archiveNumber &&
            Number(episode.episodeNumber || 0) === episodeNumber,
        }));
    }

    const unlockedEpisodeCount = Number(archiveState?.unlockedEpisodeCount || 0);
    if (!Number.isInteger(unlockedEpisodeCount) || unlockedEpisodeCount < 1) return [];

    const episodes = [];
    for (let sequenceNumber = 1; sequenceNumber <= unlockedEpisodeCount; sequenceNumber += 1) {
      const episode = getArchivePositionFromSequence(sequenceNumber, archiveDefinitions);
      if (!episode?.isAuthored) continue;
      episodes.push({
        ...episode,
        title: "",
        imageUrl: String(episode.imageUrl || "").trim(),
        label: `Archive ${episode.archiveNumber} Episode ${String(episode.episodeNumber).padStart(2, "0")}`,
        path: buildArchiveEpisodePath(episode.archiveNumber, episode.episodeNumber),
        isCurrent:
          Number(episode.archiveNumber || 0) === archiveNumber &&
          Number(episode.episodeNumber || 0) === episodeNumber,
      });
    }

    return episodes;
  }, [archiveDefinitions, archiveNumber, archiveState?.unlockedEpisodeCount, archiveState?.unlockedEpisodes, episodeNumber]);
  const currentUnlockedEpisodeIndex = useMemo(
    () =>
      unlockedEpisodes.findIndex(
        (episode) => episode.archiveNumber === archiveNumber && episode.episodeNumber === episodeNumber
      ),
    [archiveNumber, episodeNumber, unlockedEpisodes]
  );
  const archiveUnlockedEpisodes = useMemo(
    () =>
      unlockedEpisodes.filter(
        (episode) => Number(episode.archiveNumber || 0) === archiveNumber
      ).sort(
        (left, right) => Number(right?.episodeNumber || 0) - Number(left?.episodeNumber || 0)
      ),
    [archiveNumber, unlockedEpisodes]
  );
  const currentArchiveEpisodeIndex = useMemo(
    () =>
      archiveUnlockedEpisodes.findIndex(
        (episode) => Number(episode.episodeNumber || 0) === episodeNumber
      ),
    [archiveUnlockedEpisodes, episodeNumber]
  );
  const archiveEpisodePageCount = useMemo(
    () => Math.max(1, Math.ceil(archiveUnlockedEpisodes.length / Math.max(1, episodesPerPage))),
    [archiveUnlockedEpisodes.length, episodesPerPage]
  );
  const paginatedArchiveEpisodes = useMemo(() => {
    const startIndex = archiveEpisodePage * episodesPerPage;
    return archiveUnlockedEpisodes.slice(startIndex, startIndex + episodesPerPage);
  }, [archiveEpisodePage, archiveUnlockedEpisodes, episodesPerPage]);
  const currentArchiveTitle = useMemo(() => {
    return formatArchiveHeadingTitle(
      String(
        archiveUnlockedEpisodes[0]?.archiveTitle ||
          episodePayload?.archiveTitle ||
          archiveState?.currentArchiveTitle ||
          `Archive ${archiveNumber}`
      )
    );
  }, [
    archiveNumber,
    archiveState?.currentArchiveTitle,
    archiveUnlockedEpisodes,
    episodePayload?.archiveTitle,
  ]);
  const currentArchiveEpisodeName = useMemo(
    () => formatArchiveEpisodeTitle(String(episodePayload?.title || "")),
    [episodePayload?.title]
  );
  const previousEpisode = useMemo(() => {
    if (currentUnlockedEpisodeIndex <= 0) return null;
    return unlockedEpisodes[currentUnlockedEpisodeIndex - 1] || null;
  }, [currentUnlockedEpisodeIndex, unlockedEpisodes]);
  const nextEpisode = useMemo(() => {
    if (currentUnlockedEpisodeIndex < 0 || currentUnlockedEpisodeIndex >= unlockedEpisodes.length - 1) {
      return null;
    }
    return unlockedEpisodes[currentUnlockedEpisodeIndex + 1] || null;
  }, [currentUnlockedEpisodeIndex, unlockedEpisodes]);
  const previousEpisodePath = previousEpisode?.path || "";
  const previousEpisodeLabel = previousEpisode?.title || previousEpisode?.label || "";
  const computedNextEpisodePath = nextEpisode?.path || "";
  const computedNextEpisodeLabel = nextEpisode?.title || nextEpisode?.label || "";

  useEffect(() => {
    if (!archiveUnlockedEpisodes.length) {
      setArchiveEpisodePage(0);
      lastSyncedArchivePageKey.current = "";
      return;
    }

    const syncKey = [
      archiveNumber,
      episodeNumber,
      episodesPerPage,
      archiveUnlockedEpisodes.length,
      archiveUnlockedEpisodes[0]?.path || "",
      archiveUnlockedEpisodes[archiveUnlockedEpisodes.length - 1]?.path || "",
    ].join(":");
    if (lastSyncedArchivePageKey.current === syncKey) return;

    const targetIndex = currentArchiveEpisodeIndex >= 0 ? currentArchiveEpisodeIndex : 0;
    const targetPage = Math.floor(targetIndex / Math.max(1, episodesPerPage));
    lastSyncedArchivePageKey.current = syncKey;
    setArchiveEpisodePage((current) => {
      if (current === targetPage) return current;
      return targetPage;
    });
  }, [archiveNumber, archiveUnlockedEpisodes, currentArchiveEpisodeIndex, episodeNumber, episodesPerPage]);
  useEffect(() => {
    devLog("archive", "episode_progression_page_changed", {
      archiveNumber,
      episodeNumber,
      archiveTitle: currentArchiveTitle,
      page: archiveEpisodePage + 1,
      totalPages: archiveEpisodePageCount,
      visibleEpisodes: paginatedArchiveEpisodes.map((item) => ({
        archiveNumber: item.archiveNumber,
        episodeNumber: item.episodeNumber,
        isCurrent: item.isCurrent,
      })),
    });
  }, [
    archiveEpisodePage,
    archiveEpisodePageCount,
    archiveNumber,
    currentArchiveTitle,
    episodeNumber,
    paginatedArchiveEpisodes,
  ]);

  if (loading) return <div className="px-4 py-6 text-white">Loading archive record...</div>;
  if (error) return <div className="p-6 text-red-400">{error}</div>;

  if (!episodePayload) {
    return (
      <section className="mx-auto max-w-4xl px-4 py-8 text-white">
        <div className="rounded-3xl border border-[#202632] bg-[#0a0f18] p-6">
          <p className="text-xs uppercase tracking-[0.3em] text-[#c6b894]">Archive Record</p>
          <h1 className="mt-4 text-3xl font-bold">Episode not authored yet</h1>
          <p className="mt-4 text-[#b6b1a3]">
            Archive {archiveNumber} Episode {String(Math.max(1, episodeNumber)).padStart(2, "0")} is
            not present in the current authored source.
          </p>
          <Link
            to="/agency/dashboard"
            className="mt-6 inline-flex rounded-full border border-[#353b49] px-4 py-2 text-sm font-semibold text-[#d5cec0] hover:bg-white/5"
          >
            Back to Dashboard
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-6xl px-4 pb-10 pt-4 text-[#eceae4]">
      <div className="rounded-3xl border border-[#202632] bg-[radial-gradient(circle_at_80%_0%,rgba(74,58,30,0.22),transparent_35%),#0a0f18] p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.32em] text-[#c6b894]">
              Archive {archiveNumber} / Episode {String(episodeNumber).padStart(2, "0")}
            </p>
            <h1 className="mt-4 text-4xl font-black text-[#efede7]">
              {episodePayload?.title || `Archive ${archiveNumber} Episode ${String(episodeNumber).padStart(2, "0")}`}
            </h1>
          </div>
          <Link
            to="/agency/dashboard"
            className="inline-flex rounded-full border border-[#353b49] px-4 py-2 text-sm font-semibold text-[#d5cec0] transition hover:bg-white/5"
          >
            Back to Dashboard
          </Link>
        </div>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-[#b6b1a3]">
          {episodePayload?.excerpt || "Imported archive content is available for review here."}
        </p>

        {episodePayload?.imageUrl ? (
          <div className="mt-6 overflow-hidden rounded-3xl border border-[#2b303c] bg-[#101520]/55">
            <img
              src={episodePayload.imageUrl}
              alt={`${episodePayload.title || "Archive episode"} visual evidence`}
              className="aspect-[16/7] w-full object-cover"
            />
          </div>
        ) : null}

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-[#2b303c] bg-[#101520]/55 p-4">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#9f9a8e]">Current Archive</p>
            <p className="mt-2 text-lg font-semibold">{currentArchiveTitle}</p>
          </div>
          <div className="rounded-2xl border border-[#2b303c] bg-[#101520]/55 p-4">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#9f9a8e]">
              {formatArchiveEpisodeTag(episodeNumber)}
            </p>
            <p className="mt-2 text-lg font-semibold">
              {currentArchiveEpisodeName || formatArchiveEpisodeTitle(String(episodePayload?.title || ""))}
            </p>
          </div>
          <div className="rounded-2xl border border-[#2b303c] bg-[#101520]/55 p-4">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#9f9a8e]">Next Archive Update</p>
            <p className="mt-2 text-lg font-semibold">{archiveState?.nextUnlockLabel || "Unknown"}</p>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-stretch">
        {briefingVideoEmbedUrl ? (
          <article className="overflow-hidden rounded-3xl border border-[#202632] bg-[#0a0f18] lg:flex lg:h-[420px] lg:flex-col">
            <div className="border-b border-[#2b303c] px-5 py-4">
              <p className="text-xs uppercase tracking-[0.3em] text-[#c6b894]">
                Detective Briefing
              </p>
            </div>
            <div className="aspect-video lg:min-h-0 lg:flex-1 lg:aspect-auto">
              <iframe
                src={briefingVideoEmbedUrl}
                title={`${episodePayload?.title || "Archive episode"} briefing`}
                className="h-full w-full"
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
              />
            </div>
          </article>
        ) : (
          <div className="hidden lg:block" />
        )}

        <aside
          className={`hidden rounded-3xl border border-[#202632] bg-[#0a0f18] p-6 lg:block ${
            briefingVideoEmbedUrl ? "lg:flex lg:h-[420px] lg:flex-col lg:overflow-hidden" : ""
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.32em] text-[#c6b894]">Progression</p>
              <h2 className="mt-3 text-2xl font-bold text-[#efede7]">{currentArchiveTitle}</h2>
            </div>
            <div className="shrink-0 text-right">
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#9f9a8e]">Page</p>
              <p className="mt-1 text-sm font-semibold text-[#efede7]">
                {archiveEpisodePage + 1} of {archiveEpisodePageCount}
              </p>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setArchiveEpisodePage((current) => Math.max(0, current - 1))}
              disabled={archiveEpisodePage <= 0}
              className="rounded-full border border-[#353b49] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#d5cec0] transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() =>
                setArchiveEpisodePage((current) =>
                  Math.min(archiveEpisodePageCount - 1, current + 1)
                )
              }
              disabled={archiveEpisodePage >= archiveEpisodePageCount - 1}
              className="rounded-full border border-[#353b49] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#d5cec0] transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>

          <div className="mt-4 flex flex-col gap-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:pr-1">
            {paginatedArchiveEpisodes.map((unlockedEpisode) => {
              const episodeImageUrl = String(
                unlockedEpisode.imageUrl || (unlockedEpisode.isCurrent ? episodePayload?.imageUrl || "" : "")
              ).trim();

              return (
                <div
                  key={unlockedEpisode.path}
                  className="relative overflow-hidden rounded-[1.35rem] border border-[#2f3747] bg-[#111722] min-h-[8.2rem]"
                >
                  {episodeImageUrl ? (
                    <>
                      <div
                        className="absolute inset-0 bg-cover bg-center opacity-55 saturate-[0.78]"
                        style={{ backgroundImage: `url("${episodeImageUrl}")` }}
                      />
                      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(5,10,18,0.94),rgba(7,12,21,0.8)_42%,rgba(5,10,18,0.9))]" />
                    </>
                  ) : (
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(76,62,34,0.18),transparent_40%),linear-gradient(180deg,rgba(17,23,34,0.96),rgba(10,15,24,0.98))]" />
                  )}
                  <div className="relative z-10 flex h-full flex-col justify-between gap-3 p-4">
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-[0.28em] text-[#cbb688]">
                        {formatArchiveEpisodeTag(unlockedEpisode.episodeNumber)}
                      </p>
                      <p className="mt-2 max-w-[15rem] text-lg font-bold leading-tight text-[#f4efe6]">
                        {formatArchiveEpisodeTitle(unlockedEpisode.title) || unlockedEpisode.label}
                      </p>
                    </div>
                    <Link
                      to={unlockedEpisode.path}
                      className={`inline-flex w-fit rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] transition ${
                        unlockedEpisode.isCurrent
                          ? "border-[#8f7a4e] bg-[#2a2114]/90 text-[#f0e5cb] hover:bg-[#3a2c18]/95"
                          : "border-[#41506a] bg-[#111926]/88 text-[#eef1f7] hover:bg-white/10"
                      }`}
                    >
                      {unlockedEpisode.isCurrent ? "Open Current Episode" : "Open Episode"}
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </aside>
      </div>

      <article className="mt-6 rounded-3xl border border-[#202632] bg-[#0a0f18] p-6">
        <p className="text-xs uppercase tracking-[0.32em] text-[#c6b894]">Episode Record</p>
        <h2 className="mt-4 text-2xl font-bold text-[#efede7]">Archive Reading Surface</h2>
        <div className="mt-6 space-y-5 text-[1.04rem] leading-8 text-[#ddd8ca]">
          {contentBlocks.length === 0 ? (
            <p className="text-[#b6b1a3]">Episode text is unavailable.</p>
          ) : (
            contentBlocks.map((block) =>
              block.type === "scene_break" ? (
                <div key={block.id} className="my-9 flex items-center justify-center">
                  {/* Previous subtle scene divider:
                  <div className="mx-auto my-8 h-px w-16 bg-gradient-to-r from-transparent via-[#8f7a4e] to-transparent" />
                  */}
                  <div className="h-px w-20 bg-gradient-to-r from-transparent via-[#8f7a4e]/70 to-[#b79d62]/85" />
                  <div className="mx-3 h-2.5 w-2.5 rotate-45 rounded-[1px] border border-[#b79d62]/75 bg-[#1a140d] shadow-[0_0_0_2px_rgba(10,15,24,0.65)]" />
                  <div className="h-px w-20 bg-gradient-to-r from-[#b79d62]/85 via-[#8f7a4e]/70 to-transparent" />
                </div>
              ) : (
                <p key={block.id} className="whitespace-pre-wrap">
                  {renderArchiveInlineFormatting(block.content)}
                </p>
              )
            )
          )}
        </div>

        <div className="mt-8 grid gap-3 md:grid-cols-3">
          {previousEpisodePath ? (
            <Link
              to={previousEpisodePath}
              className="rounded-2xl border border-[#353b49] bg-[#101520]/55 px-4 py-4 text-left transition hover:bg-white/5"
            >
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#9f9a8e]">Previous Episode</p>
              <p className="mt-2 text-base font-semibold text-[#efede7]">
                {previousEpisodeLabel || "Previous unlocked episode"}
              </p>
            </Link>
          ) : (
            <div className="rounded-2xl border border-[#242b38] bg-[#0d131e] px-4 py-4 text-left opacity-50">
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#777264]">Previous Episode</p>
              <p className="mt-2 text-base font-semibold text-[#9e998c]">No earlier episode</p>
            </div>
          )}

          <Link
            to="/agency/dashboard"
            className="rounded-2xl border border-[#5e5443] bg-gradient-to-b from-[#3a3327] to-[#26231f] px-4 py-4 text-left transition hover:brightness-110"
          >
            <p className="text-[11px] uppercase tracking-[0.24em] text-[#cfc2a4]">Dashboard</p>
            <p className="mt-2 text-base font-semibold text-[#f0e5cb]">Back to Dashboard</p>
          </Link>

          {computedNextEpisodePath ? (
            <Link
              to={computedNextEpisodePath}
              className="rounded-2xl border border-[#353b49] bg-[#101520]/55 px-4 py-4 text-left transition hover:bg-white/5"
            >
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#9f9a8e]">Next Episode</p>
              <p className="mt-2 text-base font-semibold text-[#efede7]">
                {computedNextEpisodeLabel || "Next unlocked episode"}
              </p>
            </Link>
          ) : (
            <div className="rounded-2xl border border-[#242b38] bg-[#0d131e] px-4 py-4 text-left opacity-50">
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#777264]">Next Episode</p>
              <p className="mt-2 text-base font-semibold text-[#9e998c]">No later unlocked episode</p>
            </div>
          )}
        </div>
      </article>
    </section>
  );
};

export default ClubArchiveEpisodePage;
